import { OOUserDocumentModel } from '@features/oneOnboarding/models/UserDocumentModel';

import { OODocumentations, OOPages, OOSteps, StepStatusEnum } from '../interfaces';

import { OODocumentationModel } from './DocumentationModel';
import { JumpCondition, OOPageModel } from './FormModel';
import { OOStepElementModel } from './StepElementModel';

export enum StepRole {
  candidate = 'candidate',
  recruiter = 'recruiter',
  recruiterOrCandidate = 'candidate-or-recruiter',
  system = 'system',
}

export type minOptionalDocumentationsCondition = {
  control: { [key: string]: any };
  value?: any;
  notValue?: any;
};

export interface DocumentsForSignatureModel {
  name: string;
  isRecruiterSignatureRequired: boolean;
  type: string;
  label: string;
  stepId: string;
  controls: any[];
  generated?: boolean;
  sequence: number;
  description: string;
  signatureAnchors: any[];
  needPersonalization: boolean;
  sendIfHasNoSignature: boolean;
  isCandidateSignatureRequired: boolean;
  candidateAcknowledgementRequired: boolean;
  userDocuments: OOUserDocumentModel[];
  additionalData?: any;
}

export type WorkproModules = {
  value?: string;
  code?: string;
};

interface ConstructorParams {
  name: OOSteps;
  label: string;
  recruiterLabel: string;
  nextSteps: OOSteps[];
  elements: OOStepElementModel[];
  feature: string;
  duration: number;
  durationLabel: string;
  description: string;
  pages?: OOPageModel[];
  documentations?: OODocumentationModel[];
  documentsForSignature?: any[];
  role: StepRole;
  hideStatus: boolean;
  skipStep?: boolean;
  stepAutoComplete?: boolean;
  isVisibleCandidate?: boolean;
  isVisibleRecruiter?: boolean;
  minOptionalDocumentations?: number;
  minOptionalDocumentationsCondition?: minOptionalDocumentationsCondition[];
  handleGeneratedDocumentsForSignature?: boolean;
  uploadEarly?: boolean;
  completeCondition?: Array<Record<string, any>>;
  minOptionalDocumentationErrorMessage?: string;
  jumpCondition?: JumpCondition;
  workProModules?: Array<WorkproModules>;
}

export class OOStepModel {
  public name: OOSteps;
  public label: string;
  public recruiterLabel: string;
  public nextSteps: OOSteps[];
  public feature: string;
  public duration: number;
  public durationLabel: string;
  public description: string;
  public elements: OOStepElementModel[];
  public pages: OOPageModel[];
  public documentations: OODocumentationModel[];
  public documentsForSignature: DocumentsForSignatureModel[];
  public role: StepRole;
  public hideStatus: boolean;
  public status?: StepStatusEnum;
  public hide?: boolean;
  public skipStep?: boolean;
  public stepAutoComplete?: boolean;
  public isVisibleCandidate?: boolean;
  public isVisibleRecruiter?: boolean;
  public handleGeneratedDocumentsForSignature?: boolean;
  public uploadEarly?: boolean;
  public minOptionalDocumentations?: number;
  minOptionalDocumentationsCondition?: minOptionalDocumentationsCondition[];
  public completeCondition?: Array<Record<string, any>>;
  public minOptionalDocumentationErrorMessage?: string;
  public jumpCondition?: JumpCondition;
  public workProModules?: Array<WorkproModules>;
  public importFields?: Array<any>;
  public importControls?: Array<any>;

  constructor({
    name,
    label,
    recruiterLabel,
    nextSteps,
    feature,
    description,
    elements,
    pages,
    documentations,
    documentsForSignature,
    role,
    hideStatus,
    duration,
    durationLabel,
    skipStep,
    stepAutoComplete,
    isVisibleCandidate,
    isVisibleRecruiter,
    minOptionalDocumentations,
    handleGeneratedDocumentsForSignature,
    uploadEarly,
    completeCondition,
    minOptionalDocumentationErrorMessage,
    minOptionalDocumentationsCondition,
    jumpCondition,
    workProModules,
  }: ConstructorParams) {
    this.name = name;
    this.label = label;
    this.recruiterLabel = recruiterLabel;
    this.nextSteps = nextSteps;
    this.skipStep = skipStep;
    this.feature = feature;
    this.description = description;
    this.elements = elements;
    this.duration = duration;
    this.durationLabel = durationLabel;
    this.pages = pages ?? [];
    this.documentations = documentations ?? [];
    this.documentsForSignature = documentsForSignature ?? [];
    this.role = role;
    this.hideStatus = hideStatus;
    this.stepAutoComplete = stepAutoComplete;
    this.isVisibleCandidate = isVisibleCandidate;
    this.isVisibleRecruiter = isVisibleRecruiter;
    this.minOptionalDocumentations = minOptionalDocumentations;
    this.minOptionalDocumentationsCondition = minOptionalDocumentationsCondition;
    this.uploadEarly = uploadEarly;
    this.handleGeneratedDocumentsForSignature = handleGeneratedDocumentsForSignature;
    this.completeCondition = completeCondition;
    this.minOptionalDocumentationErrorMessage = minOptionalDocumentationErrorMessage;
    this.jumpCondition = jumpCondition;
    this.workProModules = workProModules;
  }

  public static fromJson(json: any) {
    const {
      name,
      label,
      recruiterLabel,
      nextSteps,
      feature,
      description,
      elements,
      role,
      documentsForSignature,
      hideStatus,
      duration,
      durationLabel,
      skipStep,
      stepAutoComplete,
      isVisibleCandidate,
      isVisibleRecruiter,
      minOptionalDocumentations,
      minOptionalDocumentationsCondition,
      handleGeneratedDocumentsForSignature,
      uploadEarly,
      completeCondition,
      minOptionalDocumentationErrorMessage,
      jumpCondition,
      workProModules,
    } = json;
    const pages = (json.pages || []).map((page: Record<string, any>) => OOPageModel.fromJson(page));
    const documentations = (json.documentations || []).map((doc: Record<string, any>) =>
      OODocumentationModel.fromJson(doc),
    );
    return new OOStepModel({
      name,
      label,
      recruiterLabel,
      nextSteps,
      feature,
      description,
      elements,
      pages: pages,
      documentations,
      role,
      documentsForSignature,
      hideStatus,
      duration,
      durationLabel,
      skipStep,
      stepAutoComplete,
      isVisibleCandidate,
      isVisibleRecruiter,
      minOptionalDocumentations,
      minOptionalDocumentationsCondition,
      handleGeneratedDocumentsForSignature,
      uploadEarly,
      completeCondition,
      minOptionalDocumentationErrorMessage,
      jumpCondition,
      workProModules,
    });
  }

  public getForm(formId: any) {
    return this.pages.find((form) => form.name === formId);
  }

  public getFirstForm() {
    if (this.pages.length === 0) return null;
    return this.pages[0];
  }

  public getLastForm() {
    if (this.pages.length === 0) {
      return null;
    }
    return this.pages[this.pages.length - 1];
  }

  public getPreviousForm(formName: OOPages | null) {
    if (!formName) return null;
    const index = this.pages.findIndex((form) => form.name === formName);
    if (index === -1 || index === 0) {
      return null;
    }
    return this.pages[index - 1];
  }

  public getFirstDocument() {
    if (this.documentations.length === 0) return null;
    return this.documentations[0];
  }

  public getLastDocument() {
    if (this.documentations.length === 0) {
      return null;
    }
    return this.documentations[this.documentations.length - 1];
  }

  public getNextDocument(docName: OODocumentations | null) {
    if (!docName) return null;
    const index = this.documentations.findIndex((doc) => doc.name === docName);
    if (index === -1 || index === this.documentations.length - 1) {
      return null;
    }
    return this.pages[index + 1];
  }

  public getPreviousDocument(docName: OODocumentations | null) {
    if (!docName) return null;
    const index = this.documentations.findIndex((doc) => doc.name === docName);
    if (index === -1 || index === 0) {
      return null;
    }
    return this.pages[index - 1];
  }
}
